import { z } from 'zod';

import { EmailSchema } from '@vestigas/utils';

import {
  ACTIVATION_ROUTING_CONDITION_KEYS,
  ACTIVATION_ROUTING_DOCUMENT_TYPES,
  ACTIVATION_ROUTING_REASONS,
  ACTIVATION_ROUTING_VERDICT,
} from './constants';

/**
 * A contact that satisfies a contact condition.
 */
export const zodRoutingContactSchema = z.object({
  email: EmailSchema,
  name: z.string().nullable(),
  /**
   * Reached through one of the supplier's relationships rather than owned by
   * it. Such a contact serves the customers that link it, not necessarily
   * every customer.
   */
  viaRelationship: z.boolean().default(false),
});

/**
 * One condition, as the Admin App shows it.
 */
export const zodRoutingConditionSchema = z.object({
  affectsVerdict: z.boolean().default(true),
  /** The document types this condition gates. `unlock_details` and `special_cases` apply to both, since their free-text content can't be attributed to a single one. */
  appliesTo: z.array(z.enum(ACTIVATION_ROUTING_DOCUMENT_TYPES)).default([]),
  contacts: z.array(zodRoutingContactSchema).optional(),
  currentValue: z.union([z.array(z.string()), z.string()]).nullish(),
  expected: z.string(),
  key: z.enum(ACTIVATION_ROUTING_CONDITION_KEYS),
  met: z.boolean(),
});

/**
 * Where one document type's activation email would go, and why.
 */
export const zodRoutingDocTypeVerdictSchema = z.object({
  documentType: z.enum(ACTIVATION_ROUTING_DOCUMENT_TYPES),
  /** Why this verdict is "cs", as a stable code to localize. Null when it is "direct". */
  reason: z.enum(ACTIVATION_ROUTING_REASONS).nullable(),
  verdict: z.enum(ACTIVATION_ROUTING_VERDICT),
});

/** Explains the activation-email routing decision per document type. */
export const zodActivationRoutingDiagnosisSchema = z.object({
  conditions: z.array(zodRoutingConditionSchema),
  /** The German sentence the supplier would be sent about delivery notes, or null when the channel and formats produce none. */
  dlnSentence: z.string().nullable(),
  /** Doc types with no eligible contact. The send path batches only these to CS and delivers the rest directly, so a "cs" verdict with a non-empty list here is partial. */
  documentTypesWithoutContact: z.array(z.string()),
  /** Whether the supplier half of the invoice line holds. The requesting customer must also have an invoice mailbox address configured. */
  invoiceSentenceAvailable: z.boolean(),
  invoiceSentenceHint: z.string(),
  /** Who last wrote the synced fields; "Zapier Sync" when no user is recorded. */
  notionSyncedBy: z.string().nullable(),
  notionSyncedOn: z.iso.datetime({ offset: true }).nullable(),
  notionTargetMode: z.string().nullable(),
  /** Aggregate over `verdicts`: why it is "cs", naming the most actionable reason first. Null when `verdict` is "direct". Prefer `verdicts` over this and `verdict`. */
  reason: z.enum(ACTIVATION_ROUTING_REASONS).nullable(),
  supplierAlreadyOnVestigas: z.boolean(),
  /** Aggregate over `verdicts`: "cs" when any document type routes to CS. */
  verdict: z.enum(ACTIVATION_ROUTING_VERDICT),
  /** One verdict per document type. Delivery notes and invoices are routed independently, so these can disagree; prefer them over the aggregate `verdict`/`reason` above. */
  verdicts: z.array(zodRoutingDocTypeVerdictSchema).default([]),
});
